import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
    Dialog,
    Button,
    Text,
    Callout,
    Intent,
    ProgressBar,
} from '@blueprintjs/core';
import { dismissUpdateDialog, downloadUpdate, installUpdate } from './update-actions';

// Fallback manual-download page for platforms electron-updater can't self-install
// (Linux deb/rpm/snap/flatpak). TODO: point at the real downloads page once published.
const MANUAL_DOWNLOAD_URL = 'https://bodapro.app/download';

const UpdateDialog = () => {
    const dispatch = useDispatch();
    const {
        state,
        availableVersion,
        currentVersion,
        releaseNotes,
        progressPercent,
        canAutoInstall,
        dialogDismissed,
    } = useSelector((s) => s.update);

    // Update-check/download failures (server unreachable, etc.) are handled
    // silently — logged in the main process and retried on the next cron
    // tick, with no dialog interruption. Only these states are user-facing.
    const visibleStates = ['available', 'downloading', 'downloaded'];
    const isOpen = visibleStates.includes(state) && !dialogDismissed;

    const handleClose = () => dispatch(dismissUpdateDialog());
    const handleLater = () => dispatch(dismissUpdateDialog());
    const handleUpdateNow = () => dispatch(downloadUpdate());
    const handleInstall = () => dispatch(installUpdate());
    const handleManualDownload = () => window.bodapro?.openLink?.(MANUAL_DOWNLOAD_URL);

    return (
        <Dialog
            isOpen={isOpen}
            onClose={handleClose}
            title="Software Update"
            style={{ width: '460px' }}
            canOutsideClickClose={false}
        >
            <div className="bp5-dialog-body">
                {state === 'available' && (
                    <>
                        <Text>
                            A new version of BodaPro is available: <strong>v{availableVersion}</strong>
                            {currentVersion ? ` (you're on v${currentVersion})` : ''}.
                        </Text>
                        {releaseNotes && (
                            <div style={{ marginTop: '12px', maxHeight: '160px', overflowY: 'auto' }}
                                dangerouslySetInnerHTML={{ __html: releaseNotes }} />
                        )}
                        {!canAutoInstall && (
                            <Callout intent={Intent.PRIMARY} style={{ marginTop: '16px' }}>
                                This build can't install updates automatically. Download the new version manually.
                            </Callout>
                        )}
                    </>
                )}

                {state === 'downloading' && (
                    <>
                        <Text>Downloading v{availableVersion}...</Text>
                        <ProgressBar
                            value={(progressPercent || 0) / 100}
                            intent={Intent.PRIMARY}
                            style={{ marginTop: '16px' }}
                        />
                    </>
                )}

                {state === 'downloaded' && (
                    <Text>
                        Update v{availableVersion} has been downloaded. Restart the app to finish installing it.
                    </Text>
                )}
            </div>

            <div className="bp5-dialog-footer">
                <div className="bp5-dialog-footer-actions">
                    {state === 'available' && canAutoInstall && (
                        <>
                            <Button onClick={handleLater}>Later</Button>
                            <Button intent={Intent.PRIMARY} onClick={handleUpdateNow}>Update Now</Button>
                        </>
                    )}

                    {state === 'available' && !canAutoInstall && (
                        <>
                            <Button onClick={handleLater}>Later</Button>
                            <Button intent={Intent.PRIMARY} onClick={handleManualDownload}>Download</Button>
                        </>
                    )}

                    {state === 'downloading' && (
                        <Button disabled>Downloading...</Button>
                    )}

                    {state === 'downloaded' && (
                        <>
                            <Button onClick={handleLater}>Later</Button>
                            <Button intent={Intent.PRIMARY} onClick={handleInstall}>Restart & Install</Button>
                        </>
                    )}
                </div>
            </div>
        </Dialog>
    );
};

export default UpdateDialog;
